import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { bk, nok, errMsg, custToken } from "@/lib/booking";
import { useSeo, track } from "@/lib/seo";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DAYS = [...Array(21)].map((_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; });
const Step = ({ n, title, children }) => (
  <section className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
    <h2 className="font-heading font-bold text-stone-900"><span className="mr-2 text-[#2F5D50]">{n}.</span>{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);
const chip = (on) => `rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${on ? "border-[#2F5D50] bg-[#2F5D50] text-white" : "border-stone-200 bg-white hover:border-[#2F5D50]/50"}`;

export default function Book() {
  const [params] = useSearchParams();
  const [cat, setCat] = useState(null);
  const [f, setF] = useState({ service: params.get("tjeneste") || "hjemmerenhold", sqm: 60, quantity: 10, addons: {}, code: "",
    address: "", postcode: "", details: "", allergies: "", products: "vi_tar_med", pets: false, kids: false, name: "", email: "", phone: "", payment_method: "card" });
  const [price, setPrice] = useState(null);
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState(null);
  const [start, setStart] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    bk.catalog().then((c) => { setCat(c); if (!c.allow_card) set("payment_method", "invoice"); });
    if (custToken()) bk.me().then((m) => setF((p) => ({ ...p, name: m.name, email: m.email, phone: m.phone || "", address: m.addresses?.[0] || "" }))).catch(() => {});
    if (params.get("avbrutt")) toast.info("Betalingen ble avbrutt. Tiden er ikke reservert.");
  }, [params]);
  useSeo({ title: "Bestill engangsrenhold – se pris og ledig tid | Ask-Stokke Renhold", path: "/bestill", description: "Bestill husvask, flyttevask, visningsvask, Airbnb-vask eller vindusvask på nett. Se totalpris og velg blant ekte ledige tider." });
  const [codeInput, setCodeInput] = useState("");
  const bookable = (cat?.services || []).filter((s) => s.bookable);
  const svc = bookable.find((s) => s.slug === f.service);
  const addonsFor = (cat?.addons || []).filter((a) => !a.services?.length || a.services.includes(f.service));
  const isWindow = svc?.unit === "vindu";
  const priceKey = JSON.stringify([f.service, f.sqm, f.quantity, f.addons, f.code]);

  useEffect(() => {
    if (!svc) return;
    const t = setTimeout(() => bk.price({ service: f.service, sqm: Number(f.sqm) || 0, quantity: Number(f.quantity) || 0, addons: f.addons, code: f.code })
      .then(setPrice).catch((e) => { if (f.code) { toast.error(errMsg(e)); set("code", ""); } else setPrice(null); }), 250);
    return () => clearTimeout(t);
  }, [priceKey, svc]); // eslint-disable-line react-hooks/exhaustive-deps

  const hours = price?.duration_hours;
  useEffect(() => {
    setStart(null);
    if (!day || !hours) return setSlots(null);
    bk.availability(day, hours).then((r) => setSlots(r.slots));
  }, [day, hours]);
  useEffect(() => {
    if (!/^\d{4}$/.test(f.postcode)) return setCover(null);
    bk.coverage(f.postcode).then((r) => setCover(r.covered));
  }, [f.postcode]);

  const addonQty = (a, v) => set("addons", { ...f.addons, [a.key]: Math.max(0, v) });
  const ready = price && start && cover && f.address && f.name && f.email && f.phone.length >= 8;

  const submit = async () => {
    setBusy(true);
    try {
      track("booking_submitted", { service: f.service, value: price.total, currency: "NOK", payment: f.payment_method });
      const r = await bk.create({ ...f, sqm: Number(f.sqm) || 0, quantity: Number(f.quantity) || 0, date: day, start, origin_url: window.location.origin });
      if (r.checkout_url) window.location.href = r.checkout_url;
      else window.location.href = `/bestilling/bekreftet?id=${r.booking_id}`;
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) bk.availability(day, hours).then((x) => setSlots(x.slots));
      setBusy(false);
    }
  };

  const days = useMemo(() => DAYS, []);
  if (!cat) return <div className="p-10 text-stone-500">Laster...</div>;

  return (
    <main className="mx-auto max-w-6xl px-5 py-10 grid gap-6 lg:grid-cols-[1fr_340px]" data-testid="booking-page">
      <div className="space-y-4 min-w-0">
        <div>
          <span className="rounded-full bg-[#E7EFE9] px-3 py-1 text-xs font-bold text-[#2F5D50]">Engangsrenhold · klart på 2 minutter</span>
          <h1 className="mt-3 font-heading text-4xl font-extrabold text-stone-900">Bestill renhold – like lett som å bestille mat</h1>
          <p className="mt-2 text-stone-600">Velg vask, legg til det du vil ha, velg tid – så står en renholder på døren. Du ser prisen hele veien.</p>
        </div>
        <Step n={1} title="Velg tjeneste">
          <div className="flex flex-wrap gap-2">
            {bookable.map((s) => (
              <button key={s.slug} data-testid={`book-service-${s.slug}`} className={chip(f.service === s.slug)} onClick={() => set("service", s.slug)}>{s.name}</button>
            ))}
          </div>
          <p className="mt-3 text-xs text-stone-500">Kontor, sameie eller gjentakende renhold? <a href="/fast-renholdsavtale" className="underline">Be om fast avtale</a>.</p>
        </Step>
        <Step n={2} title={isWindow ? "Antall vinduer" : "Størrelse og tillegg"}>
          <div className="flex items-center gap-3">
            <Input data-testid="book-size-input" type="number" min={1} className="w-32" value={isWindow ? f.quantity : f.sqm}
              onChange={(e) => set(isWindow ? "quantity" : "sqm", e.target.value)} />
            <span className="text-sm text-stone-600">{isWindow ? "standardvinduer" : "m²"}</span>
          </div>
          {!isWindow && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {addonsFor.map((a) => (
                <div key={a.key} className="flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 text-sm">
                  <span className="flex-1">{a.name} <span className="text-stone-500">· {nok(a.price)}</span></span>
                  {a.per_unit ? (
                    <Input data-testid={`addon-${a.key}`} type="number" min={0} className="h-8 w-16" value={f.addons[a.key] || 0} onChange={(e) => addonQty(a, Number(e.target.value))} />
                  ) : (
                    <Checkbox data-testid={`addon-${a.key}`} checked={!!f.addons[a.key]} onCheckedChange={(v) => addonQty(a, v ? 1 : 0)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </Step>
        <Step n={3} title="Adresse og detaljer">
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <Input data-testid="book-address-input" placeholder="Gateadresse" value={f.address} onChange={(e) => set("address", e.target.value)} />
            <Input data-testid="book-postcode-input" placeholder="Postnr" maxLength={4} value={f.postcode} onChange={(e) => set("postcode", e.target.value.replace(/\D/g, ""))} />
          </div>
          {cover === false && <p data-testid="coverage-error" className="mt-2 text-sm text-red-600 flex gap-1"><AlertCircle className="h-4 w-4" /> Vi dekker dessverre ikke dette postnummeret ennå.</p>}
          {cover && <p className="mt-2 text-sm text-[#2F5D50] flex gap-1"><MapPin className="h-4 w-4" /> Vi dekker området ditt.</p>}
          <Textarea data-testid="book-details-input" className="mt-3" placeholder="Hva er viktig for deg? Adkomst, nøkkel, spesielle ønsker..." value={f.details} onChange={(e) => set("details", e.target.value)} />
          <Input data-testid="book-allergies-input" className="mt-3" placeholder="Allergier, produktønsker eller andre hensyn" value={f.allergies} onChange={(e) => set("allergies", e.target.value)} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="products-ours" className={chip(f.products === "vi_tar_med")} onClick={() => set("products", "vi_tar_med")}>Dere tar med produkter</button>
            <button data-testid="products-own" className={chip(f.products === "kundens")} onClick={() => set("products", "kundens")}>Bruk mine egne produkter</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            {[["pets", "Kjæledyr i hjemmet"], ["kids", "Små barn i hjemmet"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2"><Checkbox data-testid={`book-${k}`} checked={f[k]} onCheckedChange={(v) => set(k, !!v)} />{l}</label>
            ))}
          </div>
        </Step>
        <Step n={4} title="Velg dato og tid">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {days.map((d) => (
              <button key={iso(d)} data-testid={`book-day-${iso(d)}`} onClick={() => setDay(iso(d))}
                className={`shrink-0 rounded-xl border px-3 py-2 text-center text-xs ${day === iso(d) ? "border-[#2F5D50] bg-[#2F5D50] text-white" : "border-stone-200 bg-white"}`}>
                <div className="font-bold capitalize">{d.toLocaleDateString("nb-NO", { weekday: "short" })}</div>
                <div>{d.getDate()}.{d.getMonth() + 1}</div>
              </button>
            ))}
          </div>
          {day && slots === null && <Loader2 className="mt-3 h-5 w-5 animate-spin" />}
          {slots && (slots.length ? (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-list">
              {slots.map((s) => <button key={s} data-testid={`slot-${s}`} className={chip(start === s)} onClick={() => setStart(s)}>{s}</button>)}
            </div>
          ) : <p data-testid="no-slots" className="mt-3 text-sm text-stone-500">Ingen ledige tider denne dagen. Prøv en annen dato.</p>)}
          {hours && <p className="mt-2 text-xs text-stone-500">Beregnet varighet: ca. {hours} timer</p>}
        </Step>
        <Step n={5} title="Kontaktinformasjon og betaling">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input data-testid="book-name-input" placeholder="Navn" value={f.name} onChange={(e) => set("name", e.target.value)} />
            <Input data-testid="book-email-input" type="email" placeholder="E-post" value={f.email} onChange={(e) => set("email", e.target.value)} />
            <Input data-testid="book-phone-input" placeholder="Telefon" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {cat.allow_card && <button data-testid="pay-card" className={chip(f.payment_method === "card")} onClick={() => set("payment_method", "card")}>Kort på nett</button>}
            {cat.allow_invoice && <button data-testid="pay-invoice" className={chip(f.payment_method === "invoice")} onClick={() => set("payment_method", "invoice")}>Faktura etter utført jobb</button>}
          </div>
        </Step>
      </div>

      <aside className="lg:sticky lg:top-20 h-fit rounded-2xl border border-stone-200 bg-white p-5" data-testid="price-summary">
        <h2 className="font-heading font-bold">Din pris</h2>
        {price ? (
          <div className="mt-3 space-y-1.5 text-sm">
            {price.lines.map((l, i) => <div key={i} className="flex justify-between gap-2"><span>{l.label}</span><span>{nok(l.amount)}</span></div>)}
            {price.discount > 0 && <div className="flex justify-between text-[#2F5D50]"><span>Rabattkode {price.code}</span><span>−{nok(price.discount)}</span></div>}
            {price.vat_registered && <div className="flex justify-between text-stone-500"><span>Mva</span><span>{nok(price.vat)}</span></div>}
            <div className="flex justify-between border-t border-stone-200 pt-2 font-heading text-xl font-extrabold">
              <span>Totalt</span><span data-testid="price-total">{nok(price.total)}</span>
            </div>
            <p className="text-xs text-stone-500">{price.vat_registered ? "Inkl. mva." : "Ikke mva-pliktig."} Ingen skjulte gebyrer.</p>
          </div>
        ) : <p className="mt-3 text-sm text-stone-500">Fyll inn størrelse for å se pris.</p>}
        <div className="mt-3 flex gap-2">
          <Input data-testid="discount-code-input" className="h-9" placeholder="Rabattkode" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
          <button data-testid="discount-code-apply" onClick={() => set("code", codeInput.trim())} className="rounded-full border border-stone-300 px-3 text-sm font-semibold">Bruk</button>
        </div>
        <p className="mt-2 text-xs text-stone-500">{f.payment_method === "card" ? "Bestillingen bekreftes når betalingen er gjennomført." : cat.auto_confirm ? "Bestillingen bekreftes med en gang." : "Bestillingen må godkjennes av oss – du får beskjed på e-post."}</p>
        {day && start && <p className="mt-3 text-sm font-semibold">{new Date(day).toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" })} kl. {start}</p>}
        <button data-testid="book-submit-button" disabled={!ready || busy} onClick={submit}
          className="mt-4 w-full rounded-full bg-[#2F5D50] py-3.5 font-bold text-white hover:bg-[#244a40] disabled:opacity-40 transition-opacity">
          {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : f.payment_method === "card" ? "Gå til betaling" : "Bekreft bestilling"}
        </button>
        {!ready && <p className="mt-2 text-xs text-stone-500">Fyll ut adresse, dato/tid og kontaktinfo.</p>}
      </aside>
    </main>
  );
}
